import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useLazyGetPresentacionRGRLPDFbyIdQuery } from '../../services/presentacionesApi';
import { downloadBase64File, handleErrors } from '../../utils/common';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import DownloadFileIcon from '@mui/icons-material/DownloadOutlined';
import { hideRgrlDialogAlert, showAlert } from '../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../constants/values';

export const useDownloadRGRLFilesOptionsDialog = () => {
    const dispatch = useAppDispatch();
    const [triggerGetPresentacionRGRLPDFById, { isFetching: isFetchingPDFLink }] =
        useLazyGetPresentacionRGRLPDFbyIdQuery();

    const handleRGRLDownload = async (
        idPresentacion: number,
        fileName: string,
        item: { id: number },
        typeId: number,
        conformity: boolean
    ) => {
        try {
            const response: any = await triggerGetPresentacionRGRLPDFById({
                idPresentacion,
                typeId,
                conforme: conformity
            });
            if (response?.error) {
                handleErrors(response.error);
                return;
            }

            if (response?.data?.data?.presignedUrls?.length >= 1) {
                response.data.data.presignedUrls.forEach((url: string, index: number) => {
                    downloadBase64File(url, item.id, fileName, index);
                });
            }
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo descargar el archivo',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    return { handleRGRLDownload, isFetchingPDFLink };
};

const DownloadRGRLFilesOptionsDialog = () => {
    const dispatch = useAppDispatch();
    const stateProps: any = useAppSelector((state) => state.application.rgrlDialogAlert);

    const handleClose = () => {
        dispatch(hideRgrlDialogAlert());
    };

    const open = Object.keys(stateProps).length > 0;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer
            }}
            PaperProps={{
                style: {
                    minWidth: '700px',
                    minHeight: '500px',
                    backgroundColor: '#F3F8F8',
                    borderRadius: 6
                }
            }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: 'white',
                    pt: 4,
                    pb: 4,
                    pl: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Typography
                    variant="h5"
                    sx={{ fontWeight: '700', fontSize: '20px', color: '#1E272E' }}
                >
                    {'Seleccione el tipo de archivo que desea descargar'}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        color: '#1C1B1F'
                    }}
                >
                    <CloseIcon sx={{ fontSize: 24 }} />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Card
                    data-testid={`conformity-card`}
                    sx={{
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        borderRadius: 1.5,
                        pl: 3.5,
                        pr: 3.5,
                        pt: 0.5,
                        pb: 0.5,
                        mt: 2
                    }}
                    onClick={stateProps?.handleWithoutConformityClick}
                >
                    <CardContent
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            '&:last-child': {
                                paddingBottom: '16px'
                            }
                        }}
                    >
                        <Box>
                            <Typography
                                variant="h4"
                                fontFamily={'Open Sans, sans-serif'}
                                fontSize={20}
                                mb={0.5}
                                color={'#39454E'}
                                fontWeight={400}
                            >
                                {'Descargar archivo sin conformidad'}
                            </Typography>
                            {/* <Typography
                                variant="body2"
                                fontFamily={'Open Sans, sans-serif'}
                                fontSize={14}
                                mb={0}
                                color={'#39454E'}
                                fontWeight={400}
                            >
                                {''}
                            </Typography> */}
                        </Box>
                        <Box alignContent={'center'}>
                            <Button
                                data-testid={`noConformity-button`}
                                variant="outlined"
                                sx={{
                                    minWidth: 40,
                                    minHeight: 40,
                                    borderRadius: 1.5,
                                    padding: 0,
                                    margin: 0,
                                    color: '#384A52',
                                    borderColor: '#E0EBED',
                                    '&:hover': {
                                        backgroundColor: 'white'
                                    }
                                }}
                            >
                                <DownloadFileIcon />
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
                <Card
                    data-testid={`conformity-card`}
                    sx={{
                        backgroundColor: 'white',
                        cursor: 'pointer',
                        borderRadius: 1.5,
                        pl: 3.5,
                        pr: 3.5,
                        pt: 0.5,
                        pb: 0.5,
                        mt: 2
                    }}
                    onClick={stateProps?.handleWithConformityClick}
                >
                    <CardContent
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            '&:last-child': {
                                paddingBottom: '16px'
                            }
                        }}
                    >
                        <Box>
                            <Typography
                                variant="h4"
                                fontFamily={'Open Sans, sans-serif'}
                                fontSize={20}
                                mb={0.5}
                                color={'#39454E'}
                                fontWeight={400}
                            >
                                {'Descargar archivo con conformidad'}
                            </Typography>
                            {/* <Typography
                                variant="body2"
                                fontFamily={'Open Sans, sans-serif'}
                                fontSize={14}
                                mb={0}
                                color={'#39454E'}
                                fontWeight={400}
                            >
                                {'Contiene sello'}
                            </Typography> */}
                        </Box>
                        <Box alignContent={'center'}>
                            <Button
                                data-testid={`conformity-button`}
                                variant="outlined"
                                sx={{
                                    minWidth: 40,
                                    minHeight: 40,
                                    borderRadius: 1.5,
                                    padding: 0,
                                    margin: 0,
                                    color: '#384A52',
                                    borderColor: '#E0EBED',
                                    '&:hover': {
                                        backgroundColor: 'white'
                                    }
                                }}
                            >
                                <DownloadFileIcon />
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </DialogContent>
        </Dialog>
    );
};
export { DownloadRGRLFilesOptionsDialog };
